import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { animeList, heroAnimeIds } from '@/mocks/anime';
import type { Anime } from '@/mocks/anime';

export default function HeroBanner() {
  const heroAnimes = heroAnimeIds.map((id) => animeList.find((a) => a.id === id)!).filter(Boolean);
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % heroAnimes.length);
  }, [heroAnimes.length]);

  useEffect(() => {
    const duration = 5000;
    const interval = 50;
    let elapsed = 0;

    const timer = setInterval(() => {
      elapsed += interval;
      setProgress((elapsed / duration) * 100);
      if (elapsed >= duration) {
        elapsed = 0;
        next();
      }
    }, interval);

    return () => clearInterval(timer);
  }, [current, next]);

  useEffect(() => {
    setProgress(0);
  }, [current]);

  const anime: Anime = heroAnimes[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={anime.cover}
          alt={anime.title}
          className="w-full h-full object-cover object-top transition-opacity duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-8 md:pb-14">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-500 text-white">
                  {anime.category}
                </span>
                <span className="text-white/60 text-sm">{anime.year} · {anime.season}</span>
              </div>
              <h1 className="font-heading text-2xl md:text-4xl text-white mb-2">
                {anime.title}
              </h1>
              <p className="text-white/70 text-sm md:text-base max-w-xl line-clamp-2">
                {anime.longDescription}
              </p>
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-400"></i>
                  <span className="text-white font-semibold">{anime.rating}</span>
                  <span className="text-white/50 text-sm">评分</span>
                </div>
                <div className="flex items-center gap-1 text-white/70 text-sm">
                  <i className="ri-play-circle-line"></i>
                  <span>{anime.episodes}集全</span>
                </div>
                <div className="flex items-center gap-1 text-white/70 text-sm">
                  <i className="ri-eye-line"></i>
                  <span>{(anime.views / 10000).toFixed(0)}万次播放</span>
                </div>
              </div>
              <Link
                to={`/anime/${anime.id}`}
                className="inline-flex items-center gap-2 mt-5 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-colors whitespace-nowrap"
              >
                <i className="ri-play-fill"></i>
                立即观看
              </Link>
            </div>

            <div className="hidden md:flex items-end gap-2">
              {heroAnimes.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => setCurrent(index)}
                  className={`w-16 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    index === current
                      ? 'border-primary-500 scale-110'
                      : 'border-white/30 opacity-60 hover:opacity-100'
                  }`}
                >
                  <img src={item.cover} alt={item.title} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-1.5 mt-6">
            {heroAnimes.map((_, index) => (
              <div
                key={index}
                className="h-1 rounded-full bg-white/30 overflow-hidden flex-1 max-w-16 cursor-pointer"
                onClick={() => setCurrent(index)}
              >
                <div
                  className="h-full bg-primary-500 rounded-full transition-all duration-75"
                  style={{ width: index === current ? `${progress}%` : index < current ? '100%' : '0%' }}
                ></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}